// ─────────────────────────────────────────────────────────────────────────────
// Navbar.tsx — Top navigation bar
//
// Features:
//  - Scrolls: becomes translucent + blurred after 60px scroll
//  - Front-End Framework dropdown: links to separate bundle per framework
//  - Front-End Pattern dropdown: switches MVC / MVVM / Flux / Atomic in-place
//  - Back-End Architecture dropdown: hot-swaps the active API backend
//  - Theme toggle: cycles light → dark → system
//
// Mobile (<md): the dropdowns above are desktop-only. Mobile navigation and
// customization (pages, framework, theme/layout, pattern/backend) instead
// live in MobileTabBar + MobileSheet, mounted in Layout.tsx.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Moon, Monitor, ChevronDown, Code2, Server
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  setFrontEndPattern,
  setFrontEndFramework,
  setBackEndArchitecture,
  closeMobileSheet,
} from '@/store/uiSlice';
import { resetCV } from '@/store/cvSlice';
import { useTheme } from '@/hooks/useTheme';
import { ThemeSelector } from './ThemeSelector';
import { FE_PATTERNS, FE_FRAMEWORKS, BACKENDS, frameworkHref, frameworkBadge } from '@/data/navOptions';
import type {
  FrontEndPattern, BackEndArchitecture
} from '@/types/cv.types';

// ── Reusable Dropdown ─────────────────────────────────────────────────────────
interface DropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: 'left' | 'right';
  minWidth?: string;
}

function Dropdown({ trigger, children, align = 'left', minWidth = '240px' }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 nav-link rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-navy-800 transition-colors"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {trigger}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ minWidth }}
            className={`
              absolute top-full mt-2 z-50 rounded-xl
              bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-600
              shadow-float-lg overflow-y-auto max-h-[80vh]
              ${align === 'right' ? 'right-0' : 'left-0'}
            `}
            role="listbox"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Framework Switcher ────────────────────────────────────────────────────────

function FrameworkSwitcher({ dispatch }: { dispatch: ReturnType<typeof useAppDispatch> }) {
  return (
    <div className="p-2">
      <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-3 py-2">
        Switch Framework
      </p>

      {FE_FRAMEWORKS.map((f) => {
        const isCurrent = f.value === 'react-redux';
        const badge = frameworkBadge(f, isCurrent);
        return (
          <a
            key={f.value}
            href={frameworkHref(f)}
            onClick={() => dispatch(setFrontEndFramework(f.value))}
            className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300 transition-colors"
          >
            <span className="text-sm">{f.label}</span>
            {badge && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium font-mono ${
                  isCurrent ? 'bg-accent-soft-20 text-accent' : 'bg-slate-100 dark:bg-navy-700 text-slate-500 dark:text-slate-400'
                }`}
              >
                {badge}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
}

// ── Main Navbar ───────────────────────────────────────────────────────────────
export function Navbar() {
  const dispatch   = useAppDispatch();
  const location   = useLocation();
  const { mode, resolved, toggleTheme } = useTheme();
  // 'resolved' is also passed to ThemeSelector so it knows light vs dark

  // Redux state
  const activePattern  = useAppSelector((s) => s.ui.frontEndPattern);
  const activeBackend  = useAppSelector((s) => s.ui.backEndArchitecture);

  // Whether the navbar should appear translucent (after scrolling)
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close mobile sheet on route change
  useEffect(() => { dispatch(closeMobileSheet()); }, [location, dispatch]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handlePatternChange = (pattern: FrontEndPattern) => {
    dispatch(setFrontEndPattern(pattern));
  };

  const handleBackendChange = (arch: BackEndArchitecture) => {
    dispatch(setBackEndArchitecture(arch));
    dispatch(resetCV()); // Force re-fetch from the new backend
  };

  // ── Theme icon cycles sun → moon → monitor ──────────────────────────────────
  const ThemeIcon = mode === 'light' ? Sun : mode === 'dark' ? Moon : Monitor;

  return (
    <header
      className={`hidden md:block fixed top-3 left-3 right-3 md:top-4 md:left-4 md:right-4 z-50 rounded-2xl border transition-all duration-300 ${
        scrolled ? 'backdrop-blur-md' : ''
      }`}
      style={{
        // Was hardcoded bg-white/90 dark:bg-navy-900/90 — now follows the
        // active colour scheme like everything else (see applyColourScheme).
        backgroundColor: scrolled
          ? 'color-mix(in srgb, var(--color-surface) 90%, transparent)'
          : 'transparent',
        borderColor: scrolled ? 'var(--color-border)' : 'transparent',
        boxShadow: scrolled ? 'var(--shadow-float)' : 'none',
      }}
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-16 md:h-18">

          {/* ── Logo / Wordmark ─────────────────────────────────────────── */}
          <Link
            to="/"
            className="flex items-center gap-2 font-display font-bold text-lg text-navy-900 dark:text-white hover-text-accent transition-colors"
          >
            <span className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-navy-900 text-sm font-black">
              JC
            </span>
            <span className="hidden sm:block">John Cronogue</span>
          </Link>

          {/* ── Desktop Nav ─────────────────────────────────────────────── */}
          <div className="hidden md:flex items-center gap-1">

            {/* Standard nav links */}
            <NavLink to="/"        className="nav-link rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-navy-800">CV</NavLink>
            <NavLink to="/patterns" className="nav-link rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-navy-800">Patterns</NavLink>
            {/* Shortened from "MongoDB in Docker" — that plus Projects made the
                desktop nav wrap awkwardly at ~1024px. Mobile sheet keeps the
                full label since it doesn't have the same width constraint. */}
            <NavLink to="/docker"   className="nav-link rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-navy-800">Docker</NavLink>
            <NavLink to="/projects" className="nav-link rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-navy-800">Projects</NavLink>

            <div className="w-px h-5 bg-slate-300 dark:bg-navy-600 mx-2" />

            {/* Front-End Pattern Switcher */}
            <Dropdown
              trigger={
                <>
                  <Code2 size={15} className="text-accent" />
                  <span className="text-sm leading-tight text-left">
                    FE Pattern
                    <span className="block text-[10px] text-slate-400 dark:text-slate-500">&amp; Customisation</span>
                  </span>
                </>
              }
              minWidth="280px"
            >
              {/* ── FE Pattern options ───────────────────────────────── */}
              <div className="p-2">
                <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-3 py-2">
                  F/E Pattern and Theme
                </p>
                {FE_PATTERNS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => handlePatternChange(p.value)}
                    className={`
                      w-full text-left px-3 py-2.5 rounded-lg transition-colors
                      ${activePattern === p.value
                        ? 'bg-accent-soft text-accent'
                        : 'hover:bg-slate-50 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300'
                      }
                    `}
                    role="option"
                    aria-selected={activePattern === p.value}
                  >
                    <span className="block text-sm font-medium">{p.label}</span>
                    <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{p.desc}</span>
                  </button>
                ))}
              </div>

              {/* ── Theme & Layout selector ──────────────────────────── */}
              <ThemeSelector isDark={resolved === 'dark'} />
            </Dropdown>

            {/* Frameworks Dropdown */}
            <Dropdown
              trigger={
                <>
                  <span className="text-accent">⬢</span>
                  <span className="text-sm">Frameworks</span>
                </>
              }
              minWidth="240px"
            >
              <FrameworkSwitcher dispatch={dispatch} />
            </Dropdown>

            {/* Back-End Architecture Switcher */}
            <Dropdown
              trigger={
                <>
                  <Server size={15} className="text-accent" />
                  <span className="text-sm">BE Arch</span>
                </>
              }
            >
              <div className="p-2">
                <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-3 py-2">
                  Back-End Architecture
                </p>
                {BACKENDS.map((b) => (
                  <button
                    key={b.value}
                    onClick={() => handleBackendChange(b.value)}
                    className={`
                      w-full text-left px-3 py-2.5 rounded-lg transition-colors
                      ${activeBackend === b.value
                        ? 'bg-accent-soft text-accent'
                        : 'hover:bg-slate-50 dark:hover:bg-navy-700 text-slate-700 dark:text-slate-300'
                      }
                    `}
                    role="option"
                    aria-selected={activeBackend === b.value}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{b.label}</span>
                      <span className="text-xs bg-navy-900/10 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded font-mono">{b.lang}</span>
                    </div>
                    <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{b.desc}</span>
                  </button>
                ))}
              </div>
            </Dropdown>

            <div className="w-px h-5 bg-slate-300 dark:bg-navy-600 mx-2" />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              title={`Current theme: ${mode}. Click to toggle.`}
              className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-navy-800 hover-text-accent transition-colors"
              aria-label="Toggle colour theme"
            >
              <ThemeIcon size={18} />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
